import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    CircularProgress,
    Stack,
} from '@mui/material';
import { CheckCircle, ErrorOutline, HourglassEmpty, Email } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import transport from '../../utils/axios';
import { PUBLIC_URL } from '../../config';
import { trackPaymentFailure } from '../../utils/telemetry';
import InvoiceDownloadButton from '../../components/customer/InvoiceDownloadButton';

// Source of truth for payment confirmation is the backend webhook, not the
// browser callback — the customer may close the tab before the iframe redirects.
// We poll /me/orders/{id} until the order leaves `pending`.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s

const TERMINAL_SUCCESS = new Set(['processing', 'confirmed', 'shipped', 'delivered', 'completed']);
const TERMINAL_FAILURE = new Set(['cancelled', 'refunded']);

const PaymentResult = () => {
    const { t } = useTranslation();
    const { orderId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const cancelledFlag = params.get('cancelled') === '1';
    const failedFlag = params.get('failed') === '1';

    const [order, setOrder] = useState(null);
    // failed=1 short-circuits the poll — the backend already told us the
    // payment session couldn't be started, so polling is futile.
    const [phase, setPhase] = useState(failedFlag ? 'failure' : 'polling');
    const [retrying, setRetrying] = useState(false);
    const stoppedRef = useRef(false);

    useEffect(() => {
        if (failedFlag) return undefined;
        stoppedRef.current = false;
        let attempts = 0;

        const tick = async () => {
            if (stoppedRef.current) return;
            attempts += 1;
            try {
                // skipAuthRedirect — handle 401 locally instead of letting
                // the global interceptor bounce the customer to /login mid-poll.
                const res = await transport.get(`/me/orders/${orderId}`, {
                    skipAuthRedirect: true,
                });
                const data = res.data?.data;
                if (data) setOrder(data);
                const status = data?.status;

                if (status && TERMINAL_SUCCESS.has(status)) {
                    setPhase('success');
                    return;
                }
                if (status && TERMINAL_FAILURE.has(status)) {
                    setPhase('failure');
                    return;
                }
            } catch (err) {
                // Session expired during polling — stop and surface a session
                // notice rather than silently retrying for 60s.
                if (err?.response?.status === 401) {
                    trackPaymentFailure('payment_polling_session_expired', {
                        orderId: Number(orderId),
                    });
                    setPhase('session_expired');
                    return;
                }
                // 404/transient — keep polling within budget
            }

            if (attempts >= MAX_ATTEMPTS) {
                trackPaymentFailure('payment_polling_timeout', {
                    orderId: Number(orderId),
                    reason: `${MAX_ATTEMPTS}_attempts_${POLL_INTERVAL_MS}ms`,
                });
                setPhase('timeout');
                return;
            }
            setTimeout(tick, POLL_INTERVAL_MS);
        };

        tick();
        return () => { stoppedRef.current = true; };
    }, [orderId, failedFlag]);

    const handleRetry = async () => {
        setRetrying(true);
        try {
            // The order's last failed payment id is exposed via the order
            // payload; if not, fall back to a fresh /payments/start.
            const lastPaymentId = order?.last_payment?.id || order?.payments?.[order.payments.length - 1]?.id;
            let redirectUrl = null;

            if (lastPaymentId) {
                const r = await transport.post(`/payments/${lastPaymentId}/retry`);
                redirectUrl = r.data?.data?.redirect_url;
            }
            if (!redirectUrl) {
                const r = await transport.post('/payments/start', {
                    order_id: Number(orderId),
                    provider: 'paymob',
                    meta: {
                        return_url: `${PUBLIC_URL}/checkout/${orderId}/result`,
                        cancel_url: `${PUBLIC_URL}/checkout/${orderId}/result?cancelled=1`,
                    },
                });
                redirectUrl = r.data?.data?.redirect_url;
            }

            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                setRetrying(false);
            }
        } catch (_) {
            setRetrying(false);
        }
    };

    if (phase === 'polling') {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <CircularProgress sx={{ mb: 3 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                        {cancelledFlag
                            ? t('checkout.paymentCheckingCancel', 'جاري التحقق من حالة الدفع...')
                            : t('checkout.paymentChecking', 'جاري تأكيد الدفع...')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('checkout.paymentCheckingHint', 'لا تغلق هذه الصفحة، الأمر يستغرق ثوانٍ قليلة.')}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (phase === 'success') {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        {t('checkout.orderPlaced', 'تم تأكيد طلبك بنجاح!')}
                    </Typography>
                    {order?.order_number && (
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            {t('checkout.orderNumber', 'رقم الطلب')}: <strong>{order.order_number}</strong>
                        </Typography>
                    )}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', mb: 4 }}>
                        <Email sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                            {t('checkout.confirmationEmailSent', 'تم إرسال رسالة تأكيد إلى بريدك الإلكتروني.')}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                        {order?.id && <InvoiceDownloadButton orderId={order.id} variant="contained" size="medium" />}
                        <Button variant="outlined" onClick={() => navigate('/orders')}>
                            {t('checkout.viewOrder', 'عرض الطلب')}
                        </Button>
                        <Button variant="text" onClick={() => navigate('/')}>
                            {t('cart.continueShopping', 'متابعة التسوق')}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (phase === 'failure') {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <ErrorOutline sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        {t('checkout.paymentFailed', 'فشل الدفع')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        {t('checkout.paymentFailedHint', 'لم يكتمل الدفع. يمكنك المحاولة مرة أخرى أو اختيار طريقة دفع مختلفة.')}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                        <Button variant="contained" onClick={handleRetry} disabled={retrying}>
                            {retrying ? <CircularProgress size={20} /> : t('checkout.retryPayment', 'إعادة محاولة الدفع')}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/orders')}>
                            {t('checkout.viewOrder', 'عرض الطلب')}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (phase === 'session_expired') {
        // 401 mid-poll. We deliberately don't punt to the global interceptor
        // here so the customer can finish reading the page and act on it.
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <ErrorOutline sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        {t('checkout.sessionExpired', 'انتهت الجلسة')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        {t(
                            'checkout.sessionExpiredHint',
                            'انتهت جلستك أثناء التحقق من الدفع. سجّل الدخول مرة أخرى وافتح طلباتك للاطلاع على الحالة الحالية.'
                        )}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                        <Button variant="contained" onClick={() => navigate('/login')}>
                            {t('auth.signIn', 'تسجيل الدخول')}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/orders')}>
                            {t('checkout.viewOrder', 'عرض الطلب')}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    // timeout — webhook hasn't landed within the budget. The order may still
    // succeed; we point the customer to their orders list and email.
    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <HourglassEmpty sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    {t('checkout.paymentPending', 'الدفع قيد المعالجة')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    {t('checkout.paymentPendingHint', 'مازلنا نؤكد عملية الدفع. ستتلقى بريدًا إلكترونيًا فور الانتهاء، ويمكنك متابعة الحالة من صفحة طلباتي.')}
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                    <Button variant="contained" onClick={() => navigate('/orders')}>
                        {t('checkout.viewOrder', 'عرض الطلب')}
                    </Button>
                    <Button variant="text" onClick={() => navigate('/')}>
                        {t('cart.continueShopping', 'متابعة التسوق')}
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
};

export default PaymentResult;
